import { PipelineConfigSchemaType } from "../schemas/OrionConfig";
import { ObjectId, PostRequest, Status } from "./common";

export interface BaseObjectDb {
  _id: ObjectId;
  insertion_date: string;
  updated_date: string;
  company_id: string;
  workspace_id: string;
}

export interface BaseRequestDb extends BaseObjectDb {
  status: Status;
}

interface BaseMetadata {
  creation_date: string;
  updated_date: string;
}

interface TextractBaseMetadata extends BaseMetadata {
  status: Status;
}

interface TextractErrorMetadata extends TextractBaseMetadata {
  error: string;
  status: Status.ERROR;
}

export interface Document extends BaseObjectDb {
  bucket: string;
  bucket_key: string;
  company_id: string;
  name: string;
  metadata: Record<string, any>;
  sqs_notification_names: string | null;
  textract: TextractBaseMetadata | TextractErrorMetadata;
  page_subset: number[] | null;
}

// CONFIG

export enum Language {
  FRANCAIS = "french",
  ENGLISH = "english",
  DEUTSCH = "german",
}

export interface Query {
  name?: string;
  id: string;
  type: ExtractionType | ClassificationType;
}

export interface RoutedQueries<T extends Query> {
  queries: T[];
  routes: string[];
}

/// ROUTING

export interface QueryRoute {
  id: string;
  key: string;
  goal: string;
}

/// SPLIT - CLASSIFICATION

export enum ClassificationType {
  LLM = "LLM",
}

export interface Classe {
  name: string;
  description: string | null;
}

export interface SegmentationConfig {
  classes: [Classe, ...Classe[]];
  default: Classe | null;
}

export interface GroupConfig {
  ghost_types: string[];
  weak_types: string[];
}

export interface SplitConfig {
  segmentation: SegmentationConfig;
  group: GroupConfig;
}

export interface ClassificationQuery extends Query {
  name: string;
  type: ClassificationType;
  classes: Classe[];
  default: Classe | null;
}

export interface ClassificationConfig {
  queries: Array<ClassificationQuery | RoutedQueries<ClassificationQuery>>;
  max_page: number;
  pages: number[] | null;
}

/// EXTRACTION

export enum ExtractionType {
  LLM = "LLM",
  REGEX = "REGEX",
  PREDEFINED = "PREDEFINED",
  COMPUTED = "COMPUTED",
  AGGREGATE = "AGGREGATE",
}

export enum RegexMatch {
  ALL = "all",
  FIRST = "first",
  LAST = "last",
  EXISTS = "exists",
}

export enum PredefinedExtraction {
  EPC = "EPC",
  ASBESTOS = "ASBESTOS",
  TWODDOC = "TWODDOC",
}

export interface CustomSchemaFieldBase {
  name: string;
  parsing_type:
    | ParsingType.Date
    | ParsingType.Float
    | ParsingType.Int
    | ParsingType.String
    | ParsingType.Boolean;
}

export type CustomSchemaField = Omit<CustomSchemaFieldBase, "parsing_type"> & {
  parsing_type:
    | ParsingType.Date
    | ParsingType.Float
    | ParsingType.Int
    | ParsingType.String
    | ParsingType.Boolean
    | ParsingType.Enum;
  enum_values: string[] | null;
};

export type CustomSchemaFieldResult = Omit<
  CustomSchemaFieldBase,
  "parsing_type"
> & {
  parsing_type: ParsingType;
  enum_values: string[] | null;
};

export interface ExtractionQueryBaseBase extends Query {
  name: string;
  parsing_type: ParsingType;
  type: ExtractionType;
  custom_schema: CustomSchemaFieldBase[] | null;
  expected_value: any | null;
}

export type ExtractionQueryBase = Omit<
  ExtractionQueryBaseBase,
  "custom_schema"
> & {
  enum_values: string[] | null;
  custom_schema: CustomSchemaField[] | null;
  expected_value: any | null;
  many: boolean;
};

export interface LLMExtractionQuery extends ExtractionQueryBase {
  query: string;
  type: ExtractionType.LLM;
}

export interface LLMRoutedExtractionParameters {
  context_description: string | null;
}

export interface LLMConfigExtractionParameters
  extends LLMRoutedExtractionParameters {
  chunk_size: number;
  sources: boolean;
}

export interface RegexExtractionQuery extends ExtractionQueryBase {
  pattern: string;
  params: RegexExtractionParameters;
  type: ExtractionType.REGEX;
}
export interface RegexExtractionParameters {
  match: RegexMatch;
  group_number: number | null;
}

export interface PredefinedQuery extends Query {
  name: string;
  query: PredefinedExtraction;
  type: ExtractionType.PREDEFINED;
  expected_value: any | null;
}

export interface ComputedQuery extends ExtractionQueryBaseBase {
  expression: string;
  type: ExtractionType.COMPUTED;
}

export interface AggregateQuery extends ExtractionQueryBaseBase {
  expression: string;
  type: ExtractionType.AGGREGATE;
}

export type ExtractionQuery =
  | PredefinedQuery
  | RegexExtractionQuery
  | LLMExtractionQuery
  | ComputedQuery
  | AggregateQuery;

export interface ExtractionRoutedQueries
  extends RoutedQueries<ExtractionQuery> {
  params: LLMRoutedExtractionParameters | null;
}

export interface ExtractionConfig {
  queries: Array<ExtractionQuery | ExtractionRoutedQueries>;
  params: LLMConfigExtractionParameters;
}

export enum ValidationSeverity {
  INFO = "info",
  WARNING = "warning",
  ERROR = "error",
  CRITICAL = "critical",
}

export interface ValidationRuleBase {
  name: string;
  severity: ValidationSeverity;
  success_message: string;
  error_message: string;
  untreatable_message: string;
}
export interface ValidationRule extends ValidationRuleBase {
  expression: string;
}

export interface LLMValidationRule extends ValidationRuleBase {
  check: string;
}

export interface ValidationConfig {
  document_rules: ValidationRule[] | null;
  asset_rules: (ValidationRule | LLMValidationRule)[] | null;
  inputs: Record<string, any>;
  language: Language | null;
}

export interface WorkflowState {
  name: string;
  is_default: boolean;
  set_on_validation_success: boolean;
  set_on_validation_failure: boolean;
  set_on_validation_error: boolean;
}
/// PIPELINE

export interface PipelineConfig {
  language: Language;
  split: SplitConfig | null;
  classification: ClassificationConfig | null;
  extraction: ExtractionConfig | null;
  validation: ValidationConfig | null;
  workflow: WorkflowState[];
  synthesis: string | null;
  routes: QueryRoute[];
}

export interface PipelineConfigDb extends BaseObjectDb {
  name: string;
  config: PipelineConfig;
}

// OUTPUT

// VALIDATION

interface ValidationDetail {
  error: string | null;
  result: any | null;
}

export interface ValidationResult {
  success: boolean | null;
  severity: ValidationSeverity;
  details: ValidationDetail | null;
  message: string | null;
}

export interface ValidationOutput {
  document_validations: Record<string, ValidationResult>[];
  asset_validations: Record<string, ValidationResult>;
}

// EXTRACTION

export enum ParsingType {
  String = "string",
  Address = "address",
  Int = "int",
  Float = "float",
  Date = "date",
  Name = "name",
  Measure = "measure",
  Currency = "currency",
  Boolean = "boolean",
  Enum = "enum",
  Custom = "custom",
}
export interface Name {
  civility: string | null;
  firstname: string | null;
  lastname: string | null;
}

export interface Address {
  number: string | null;
  way: string | null;
  city: string | null;
  postal_code: string | null;
  country: string | null;
}

export interface Currency {
  value: number | null;
  currency: string | null;
}

export interface Measure {
  value: number | null;
  unit: string | null;
}

interface ExtractionSource {
  value: string;
  page: number | null;
  position: [number, number] | null;
}

export interface ExtractionResult {
  name: string;
  source: ExtractionSource[] | null;
  value: any | null;
  expected_value: any | null;
  enum_values: string[] | null;
  custom_schema: CustomSchemaFieldResult[] | null;
  check: boolean | null;
  parsing_type: ParsingType;
}

export type ExtractionOutput = Record<string, ExtractionResult>;

// CLASSIFICATION

export type ClassificationOutput = Record<string, string>;

// COREPIPELINE

export interface CorePipelineOutput {
  document_id: string;
  classification: ClassificationOutput | null;
  extraction: ExtractionOutput | null;
  page_subset: number[] | null;
}

// PROCESSING

export interface ProcessingRequestDb extends BaseRequestDb, ProcessingRequest {
  config: PipelineConfig;
  documents: ObjectId[];
  pipelines: ObjectId[];
  result: ProcessingOutput | null;
  review: ProcessingOutput | null;
  workflow: string;
}

export interface ProcessingRequestWithConfig extends ProcessingRequestDb {
  config_name?: string;
}

export interface ProcessingOutput {
  analysis: CorePipelineOutput[];
  validation: ValidationOutput | null;
  aggregates: ExtractionOutput | null;
  synthesis: string | null;
}

// ASSET

export interface Asset extends BaseObjectDb {
  name: string;
  documents: ObjectId[];
  history: ObjectId[];
  assignee: ObjectId | null;
}

// API REQUESTS

export interface CreateAssetRequest extends PostRequest {
  name?: string;
  documents: string[];
  assignee?: ObjectId;
  config_id?: ObjectId;
  config?: PipelineConfig;
}

export interface GetDocumentResponse {
  document: Document;
  pre_signed_url: string;
}

export interface CreateAssetResponse {
  asset_id: ObjectId;
  documents: Record<string, ObjectId>;
  pre_signed_urls: Array<{
    url: string;
    fields: Record<string, any>;
  }>;
  expiration_time: number;
}

export interface AssetAddDocumentRequest {
  documents: string[];
}

export interface AssetDeleteDocumentRequest {
  documents: ObjectId[];
}

export interface AssetRenameDocumentRequest {
  documents: Record<ObjectId, string>;
}

export interface UpdateAssigneeRequest {
  assignee: ObjectId;
}

export interface UpdateReviewRequest {
  document: number;
  review: CorePipelineOutput;
}

export interface GetConfigsResponse {
  configs: PipelineConfigDb[];
}

export interface ConfigUpdateRequest {
  name: string;
  config: PipelineConfigSchemaType;
}

export interface ConfigInsertRequest extends PostRequest, ConfigUpdateRequest {
  name: string;
}

export interface ProcessingRequest {
  config_id?: ObjectId;
  config?: PipelineConfig;
  callback?: string;
  asset_id?: ObjectId;
  documents?: string[];
}

export interface ProcessingResponse {
  request_id: ObjectId;
  documents?: Record<string, ObjectId>;
  asset_id: ObjectId;
  pre_signed_urls?: Array<{
    url: string;
    fields: Record<string, any>;
  }>;
  expiration_time: number;
}

export interface UpdateWorkflowRequest {
  workflow: string;
}
